if (!customElements.get('scent-pair-bundle')) {
  customElements.define(
    'scent-pair-bundle',
    class ScentPairBundle extends HTMLElement {
      constructor() {
        super();
        this.priceEl = null;
        this.submitButton = null;
        this.errorWrapper = null;
        this.errorMessage = null;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      connectedCallback() {
        this.priceEl = this.querySelector('[data-bundle-price]');
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');

        this.addEventListener('change', this.onChange.bind(this));
        this.querySelector('[data-bundle-form]')?.addEventListener('submit', this.onSubmit.bind(this));

        this.syncAvailability();
        this.updatePrice();
        this.updateSubmitState();
      }

      getChoiceInputs() {
        return Array.from(this.querySelectorAll('[data-bundle-choice]'));
      }

      getCheckedChoices() {
        return this.getChoiceInputs().filter((input) => input.checked && !input.disabled);
      }

      onChange(event) {
        const input = event.target.closest('[data-bundle-choice]');
        if (!input) return;

        if (input.checked && this.getCheckedChoices().length > 2) {
          input.checked = false;
          this.handleErrorMessage(this.dataset.maxSelectionMessage || 'Choose 2 scents');
        } else {
          this.handleErrorMessage();
        }

        this.syncAvailability();
        this.updatePrice();
        this.updateSubmitState();
      }

      syncAvailability() {
        const selected = this.getCheckedChoices();
        const selectedVariantIds = selected.map((input) => input.dataset.variantId);
        const atLimit = selected.length >= 2;

        this.getChoiceInputs().forEach((input) => {
          if (input.hasAttribute('data-sold-out')) return;

          const shouldDisable = atLimit && !selectedVariantIds.includes(input.dataset.variantId);
          input.disabled = shouldDisable;
          input.closest('.variant-option')?.querySelector('label')?.classList.toggle('disabled', shouldDisable);
        });
      }

      getSelections() {
        return this.getCheckedChoices().map((input) => ({
          productId: input.dataset.productId,
          productTitle: input.dataset.productTitle || '',
          variantId: input.dataset.variantId,
          price: Number(input.dataset.price) || 0,
        }));
      }

      isComplete() {
        const selections = this.getSelections();
        if (selections.length !== 2) return false;
        return selections[0].variantId !== selections[1].variantId;
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = (Number(cents) / 100).toFixed(2);
        return format
          .replace(/\{\{\s*amount\s*\}\}/g, value)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(Number(cents) / 100).toString())
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(Number(cents) / 100).toString());
      }

      updatePrice() {
        if (!this.priceEl) return;
        const total = this.getSelections().reduce((sum, item) => sum + item.price, 0);
        this.priceEl.textContent = total > 0 ? this.formatMoney(total) : this.dataset.pricePlaceholder || '—';
      }

      updateSubmitState() {
        if (!this.submitButton) return;
        this.submitButton.disabled = !this.isComplete();
      }

      handleErrorMessage(message) {
        if (!this.errorWrapper || !this.errorMessage) return;
        const hasMessage = Boolean(message);
        this.errorWrapper.toggleAttribute('hidden', !hasMessage);
        this.errorMessage.textContent = message || '';
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.submitButton || !this.isComplete() || this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }
        this.error = false;

        const selections = this.getSelections();
        const pairTitle = this.dataset.pairTitle || '';
        const items = selections.map((item) => ({
          id: Number(item.variantId),
          quantity: 1,
          properties: {
            Pair: pairTitle,
          },
        }));

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        const body = { items };

        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description || response.message || window.cartStrings.error);
              this.error = true;
              return;
            }

            if (this.applyBundleDiscount()) return;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            return this.ensureCartSections(response).then((cartResponse) => {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'scent-pair-bundle',
                cartData: cartResponse,
              });

              this.cart.renderContents(cartResponse);
              this.error = false;
            });
          })
          .catch((error) => {
            console.error(error);
            this.handleErrorMessage(window.cartStrings.error);
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          });
      }

      applyBundleDiscount() {
        const code = (this.dataset.discountCode || '').trim();
        if (!code) return false;

        const redirect = window.routes?.cart_url || '/cart';
        window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`;
        return true;
      }

      ensureCartSections(response) {
        if (response?.sections) return Promise.resolve(response);
        if (!this.cart) return Promise.resolve(response);

        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id).join(',');
        return fetch(`?sections=${sectionIds}`)
          .then((res) => res.json())
          .then((sections) => ({ ...response, sections }));
      }
    }
  );
}
